/**
 * PWA Install Prompt Component
 * Shows a banner to install the app on supported devices.
 * Context-aware: shows staff-specific messaging when on /me route.
 */
import { useState, useEffect, useMemo } from 'react';
import { Download, X, Smartphone, Zap, Clock as ClockIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const location = useLocation();

  const isStaffPortal = location.pathname.startsWith('/me');

  // Compute iOS detection once (not in effect)
  const isIOS = useMemo(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
  }, []);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed, don't show prompt
    }

    // Check if user dismissed the prompt before
    // Staff portal: shorter dismissal (1 day), Admin pages: 7 days
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      const dismissalThreshold = isStaffPortal ? 1 : 7;
      if (daysSinceDismissed < dismissalThreshold) {
        return;
      }
    }

    // For iOS, show custom prompt after a delay
    if (isIOS) {
      const timer = setTimeout(() => {
        const isInStandalone = window.navigator.standalone;
        if (!isInStandalone) {
          setShowIOSPrompt(true);
        }
      }, isStaffPortal ? 2000 : 4000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop, listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), isStaffPortal ? 1500 : 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [isIOS, isStaffPortal]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  if (!showPrompt && !showIOSPrompt) return null;

  // Staff portal — use indigo gradient + clock-in messaging
  // Admin pages — use red gradient + general messaging
  const gradient = isStaffPortal
    ? 'from-indigo-600 via-purple-600 to-indigo-700'
    : 'from-red-800 to-red-900';
  const accentText = isStaffPortal ? 'text-indigo-100' : 'text-red-100';
  const buttonAccent = isStaffPortal ? 'text-indigo-700 hover:bg-indigo-50' : 'text-red-800 hover:bg-red-50';

  const title = isStaffPortal ? 'Install Portal Saya' : 'Install Underads App';
  const HeroIcon = isStaffPortal ? ClockIcon : Smartphone;

  const description = isStaffPortal
    ? (isIOS
        ? "Tambahkan ke Skrin Utama untuk clock-in lebih pantas setiap pagi. Tap butang Share, kemudian 'Add to Home Screen'."
        : "Install app ini untuk clock-in lebih pantas — guna macam app native dari skrin utama phone anda.")
    : (isIOS
        ? "Tap Share button, then 'Add to Home Screen' untuk install app ini."
        : "Install app untuk akses pantas dan pengalaman lebih baik.");

  return (
    <div data-testid="pwa-install-prompt" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className={`bg-gradient-to-br ${gradient} rounded-xl shadow-2xl p-4 text-white`}>
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg flex-shrink-0">
            <HeroIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base sm:text-lg flex items-center gap-1.5">
              {title}
              {isStaffPortal && <Zap className="w-4 h-4 text-yellow-300" />}
            </h3>
            <p className={`text-xs sm:text-sm ${accentText} mt-1 leading-snug`}>
              {description}
            </p>
            
            {isStaffPortal && (
              <ul className={`text-[11px] ${accentText} mt-2 space-y-0.5`}>
                <li>✓ Akses 1-tap dari home screen</li>
                <li>✓ Clock-in lebih pantas setiap hari</li>
                <li>✓ Tiada perlu buka browser</li>
              </ul>
            )}
            
            <div className="flex items-center gap-2 mt-3">
              {!isIOS && (
                <button
                  data-testid="pwa-install-btn"
                  onClick={handleInstall}
                  className={`flex items-center gap-2 px-4 py-2 bg-white ${buttonAccent} rounded-lg font-bold text-sm transition-colors`}
                >
                  <Download className="w-4 h-4" />
                  Install Sekarang
                </button>
              )}
              <button
                data-testid="pwa-dismiss-btn"
                onClick={handleDismiss}
                className="px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
              >
                Nanti
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Close"
            className="p-1 text-white/60 hover:text-white flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
